import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { ApiError, EventDeliveryMethod } from '../../api';
import { eventEndpoints } from '../../api';
import { Button, Panel } from '../../ui';
import type { PanelProps } from '../Event';
import { INPUT, toCents } from './shared';
import { dateWords } from '../../terms';

/**
 * Delivery methods for the event (D-58): which of eDelivery, will call and
 * mail the customer may choose at checkout, what each costs, and how many
 * days before the performance it stops being offered. An event with nothing
 * saved offers eDelivery, free.
 */

const LABELS: Record<EventDeliveryMethod['delivery_method'], { name: string; hint: string }> = {
  MOBILE: { name: 'eDelivery', hint: 'Emailed PDF and My Tickets; the QR code admits.' },
  WILL_CALL: { name: 'Will call', hint: 'Held at the window; printed in a batch or at pickup (Admin → Mail & will call, or the window).' },
  MAIL: { name: 'Mail', hint: 'The customer gives an address; tickets print in a batch with a cover ticket per order.' },
};

export function DeliveryPanel({ detail, onError, can }: PanelProps) {
  const eventId = detail.event.id;
  const editable = detail.event.status !== 'ARCHIVED' && can('events.manage');
  const methods = useQuery<{ methods: EventDeliveryMethod[] }, ApiError>({ queryKey: ['event', eventId, 'delivery-methods'], queryFn: () => eventEndpoints.deliveryMethods(eventId) });
  const [rows, setRows] = useState<{ delivery_method: EventDeliveryMethod['delivery_method']; enabled: boolean; fee: string; cutoff: string }[]>([]);
  useEffect(() => {
    if (methods.data) setRows(methods.data.methods.map((m) => ({ delivery_method: m.delivery_method, enabled: m.enabled, fee: (m.fee_cents / 100).toFixed(2), cutoff: m.cutoff_days === null ? '' : String(m.cutoff_days) })));
  }, [methods.data]);
  const save = useMutation({
    mutationFn: () => {
      const input = rows.map((r) => {
        const fee = toCents(r.fee) ?? 0;
        if (fee < 0) throw new Error(`${LABELS[r.delivery_method].name}: the fee is not a number`);
        const cutoff = r.cutoff.trim() === '' ? null : Number(r.cutoff);
        if (cutoff !== null && (!Number.isInteger(cutoff) || cutoff < 0)) throw new Error(`${LABELS[r.delivery_method].name}: cutoff days must be a whole number`);
        return { delivery_method: r.delivery_method, enabled: r.enabled, fee_cents: fee, cutoff_days: cutoff };
      });
      return eventEndpoints.setDeliveryMethods(eventId, input);
    },
    onSuccess: () => void methods.refetch(),
    onError: (cause: Error) => onError(cause as ApiError),
  });
  const set = (method: string, patch: Partial<{ enabled: boolean; fee: string; cutoff: string }>) => setRows((current) => current.map((r) => (r.delivery_method === method ? { ...r, ...patch } : r)));

  return (
    <Panel title="Delivery" description={`What the customer can choose at checkout, its fee (added to the order as a delivery-fee line, refundable by staff) and the cutoff: the number of days before the ${dateWords().one} after which it is no longer offered. Blank cutoff = until sales close. The box office window prints or holds tickets without a fee.`}>
      {methods.error ? <p className="px-5 py-4 text-sm text-bad">{methods.error.message}</p> : null}
      <div className="divide-y divide-line">
        {rows.map((row) => (
          <div key={row.delivery_method} className="flex flex-wrap items-end gap-4 px-5 py-3">
            <label className="flex w-56 items-center gap-2 text-sm text-ink-900">
              <input type="checkbox" checked={row.enabled} disabled={!editable} onChange={(e) => set(row.delivery_method, { enabled: e.target.checked })} />
              <span>
                <span className="font-medium">{LABELS[row.delivery_method].name}</span>
                <span className="block text-xs text-ink-400">{LABELS[row.delivery_method].hint}</span>
              </span>
            </label>
            <label className="text-sm">
              <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">Fee ($)</span>
              <input value={row.fee} disabled={!editable || !row.enabled} onChange={(e) => set(row.delivery_method, { fee: e.target.value })} inputMode="decimal" className={`tabular mt-1 w-24 ${INPUT}`} />
            </label>
            <label className="text-sm">
              <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">Cutoff (days before)</span>
              <input value={row.cutoff} disabled={!editable || !row.enabled} onChange={(e) => set(row.delivery_method, { cutoff: e.target.value.replace(/\D/g, '') })} inputMode="numeric" placeholder="none" className={`tabular mt-1 w-24 ${INPUT}`} />
            </label>
          </div>
        ))}
      </div>
      {editable ? (
        <div className="border-t border-line px-5 py-3">
          <Button variant="primary" disabled={save.isPending || rows.length === 0 || !rows.some((r) => r.enabled)} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save delivery methods'}
          </Button>
          {!rows.some((r) => r.enabled) && rows.length > 0 ? <span className="ml-3 text-sm text-bad">Offer at least one method.</span> : null}
        </div>
      ) : null}
    </Panel>
  );
}
